import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Phone, Menu, X, Search, User } from 'lucide-react';
import { Button } from './ui/button';
import { formatWhatsAppLink, formatPhoneNumber, PHONE_NUMBER, WHATSAPP_NUMBER } from '../utils/whatsapp';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_5793c14a-9e5d-45df-be07-4fd47ebae7d7/artifacts/63qxnlv7_16.png";

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const menuItems = [
    { label: 'Início', path: '/' },
    { label: 'Produtos', path: '/produtos' },
    { label: 'Categorias', path: '/categorias' },
    { label: 'Sobre', path: '/sobre' },
    { label: 'Contato', path: '/contato' }
  ];

  return (
    <>
      <header className={`sticky top-0 z-50 transition-shadow duration-300 ${isScrolled ? 'shadow-xl' : 'shadow-md'}`}>
        {/* Top strip - phone numbers - WHITE */}
        <div className="bg-white border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 flex justify-between items-center py-1.5">
            <div className="flex items-center gap-2 sm:gap-5 text-gray-800 text-[11px] sm:text-sm">
              <a href={`tel:${PHONE_NUMBER}`} className="flex items-center gap-1 sm:gap-1.5 hover:text-[#055509] transition-colors font-bold">
                <Phone size={11} className="sm:w-[13px] sm:h-[13px]" />
                <span>{formatPhoneNumber(PHONE_NUMBER)}</span>
              </a>
              <a href={formatWhatsAppLink()} className="flex items-center gap-1 sm:gap-1.5 hover:text-[#055509] transition-colors font-bold">
                <Phone size={11} className="sm:w-[13px] sm:h-[13px]" />
                <span><span className="hidden sm:inline">WhatsApp: </span>{formatPhoneNumber(WHATSAPP_NUMBER)}</span>
              </a>
            </div>
            <span className="hidden md:block text-sm text-[#055509] font-bold">
              Consultoria técnica especializada em componentes hidráulicos
            </span>
          </div>
        </div>

        {/* Main nav - GREEN */}
        <div className="bg-[#055509]">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            {/* Logo - bigger */}
            <Link to="/" className="flex items-center flex-shrink-0">
              <img
                src={LOGO_URL}
                alt="TerraHydro Soluções Hidráulicas"
                data-testid="header-logo"
                className="h-12 sm:h-14 w-auto"
              />
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-7">
              {menuItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className="text-white/90 hover:text-[#FFB025] font-medium transition-colors relative group text-sm"
                >
                  {item.label}
                  <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-[#FFB025] transition-all group-hover:w-full" />
                </Link>
              ))}
            </nav>

            {/* Desktop Actions */}
            <div className="hidden lg:flex items-center gap-3">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/produtos')}
                className="border-white/30 text-white hover:bg-white hover:text-[#055509] bg-transparent text-xs h-8"
              >
                <Search size={14} className="mr-1.5" />
                Buscar Peças
              </Button>
              <Button
                size="sm"
                onClick={() => window.open(formatWhatsAppLink(), '_blank')}
                className="bg-[#FFB025] text-[#055509] hover:bg-white font-semibold text-xs h-8"
              >
                Solicitar Cotação
              </Button>
              <Button
                data-testid="client-login-btn"
                variant="ghost"
                size="sm"
                onClick={() => navigate('/entrar')}
                className="text-white/70 hover:text-[#FFB025] hover:bg-white/10 text-xs h-8"
              >
                <User size={14} className="mr-1" />
                Entrar
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden p-2 text-white"
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden bg-[#055509] border-t border-white/10">
            <nav className="flex flex-col py-3">
              {menuItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="px-4 py-3 text-white/90 hover:bg-white/10 hover:text-[#FFB025] transition-colors"
                >
                  {item.label}
                </Link>
              ))}
              <div className="px-4 py-3 flex flex-col gap-2">
                <Button variant="outline" size="sm" onClick={() => { setIsMobileMenuOpen(false); navigate('/produtos'); }} className="border-white/30 text-white w-full bg-transparent hover:bg-white hover:text-[#055509]">
                  <Search size={16} className="mr-2" />Buscar Peças
                </Button>
                <Button size="sm" onClick={() => window.open(formatWhatsAppLink(), '_blank')} className="bg-[#FFB025] text-[#055509] hover:bg-white w-full font-semibold">
                  Solicitar Cotação
                </Button>
                <Button variant="ghost" size="sm" onClick={() => { setIsMobileMenuOpen(false); navigate('/entrar'); }} className="text-white/80 hover:text-[#FFB025] w-full hover:bg-white/10">
                  <User size={16} className="mr-2" />Entrar / Cadastrar
                </Button>
              </div>
            </nav>
          </div>
        )}
      </header>
    </>
  );
};

export default Header;
